/*
<summary>
   This class component is all about Managing Tool Check Item Template data.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import { IToolCheckItemFilter } from '../../models/IToolCheckItemFilter';
import {
  IAddToolCheckItemTemplate,
  IAddToolCheckItemTemplateRequest,
  IToolCheckItem,
  IToolCheckItemHeader,
  IUpdateToolCheckItemTemplateRequest,
} from '../../models/forms/IToolCheckItemTemplate';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IToolCheckItemTemplateHeaderRow,
  IToolCheckItemTemplateList,
  IToolCheckItemTemplateListVM,
  IToolCheckItemTemplateListResponseVM,
} from '../../models/response/IToolCheckItemTemplateResponse';
import { formatMessage } from '../../translations/format-message';
import {
  AddToolCheckItemTemplateInitialState,
  getToolCheckItemTemplateListInitialState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { getLanguageFromLocalStorage } from '../../helpers/localstorage-helper';
import { IOption } from '../../models/ICommon';

export class ToolCheckItemTemplateStore {
  initialStateValue = ObservableInitialState;
  checkItemTemplateList: IToolCheckItemTemplateListResponseVM =
    getToolCheckItemTemplateListInitialState;
  addUpdateCheckItemTemplateState = { ...this.initialStateValue };
  deleteCheckItemTemplateState = { ...this.initialStateValue };
  getAllCheckItemTemplateState = { ...this.initialStateValue };
  checkItemTemplateDetail: IAddToolCheckItemTemplate =
    AddToolCheckItemTemplateInitialState;
  getCheckItemTemplateByIdState = { ...this.initialStateValue };
  allToolCheckItemTemplatesState = { ...this.initialStateValue };
  allToolCheckItemTemplates:IToolCheckItemTemplateListVM[] = [];
  constructor() {
    makeObservable(this, {
      checkItemTemplateList: observable,
      addUpdateCheckItemTemplateState: observable,
      deleteCheckItemTemplateState: observable,
      getAllCheckItemTemplateState: observable,
      checkItemTemplateDetail: observable,
      getCheckItemTemplateByIdState: observable,
      allToolCheckItemTemplates: observable,
      allToolCheckItemTemplatesState: observable,
      GetCheckItemTemplateListService: action,
      AddCheckItemTemplateService: action,
      UpdateCheckItemTemplateService: action,
      DeleteCheckItemTemplateService: action,
      GetCheckItemTemplateByIdService: action,
      GetAllCheckItemTemplatesService: action,
      resetAddUpdateState: action,
      resetDeleteState: action,
      resetDetail: action,
      reset: action,
      resetAllToolCheckItemTemplatesState: action,
      getToolCheckItemTemplateList: computed,
      allToolCheckItemTemplateOptions: computed,
      resetStore: action,
    });
  }

  /**
  * This function is used to get the check item template list from API by page number and filter.
  * @param currentPage: Current page number
  * @param pageLimit: Number of records per page
  * @param searchTerm: Filter criteria for the check item template list
  */
  GetCheckItemTemplateListService = (
    currentPage: number,
    pageLimit: number,
    searchTerm: IToolCheckItemFilter
  ) => {
    this.getAllCheckItemTemplateState.inProgress = true;
    const url = `${URLConstants.GetToolCheckItemTemplateList}?PageNo=${currentPage}&PageSize=${pageLimit}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IToolCheckItemTemplateListResponseVM>>
        ) => {
          if (response.data.Error) {
            this.getAllCheckItemTemplateState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.checkItemTemplateList = response.data.Data;
          }
        }
      )
      .catch((err: any) => {
        this.getAllCheckItemTemplateState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllCheckItemTemplateState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to get the complete list of check item templates from API.
  */
  GetAllCheckItemTemplatesService = () => {
    this.allToolCheckItemTemplatesState.inProgress = true;
    const url = URLConstants.GetToolCheckitemTemplateAll;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IToolCheckItemTemplateListVM[]>>
        ) => {
          if (response.data.Error) {
            this.allToolCheckItemTemplatesState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.allToolCheckItemTemplates = response.data.Data;
          }
        }
      )
      .catch((err: any) => {
        this.allToolCheckItemTemplatesState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.allToolCheckItemTemplatesState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to add a new check item template.
  * @param data: Check item template details to be added
  */
  AddCheckItemTemplateService = (data: IAddToolCheckItemTemplateRequest) => {
    this.addUpdateCheckItemTemplateState.inProgress = true;
    return baseService
      .postRequest(URLConstants.AddToolCheckItemTemplate, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCheckItemTemplateState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addUpdateCheckItemTemplateState.success = true;
          toast.success(formatMessage('added_success'));
        }
      })
      .catch((err: any) => {
        this.addUpdateCheckItemTemplateState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateCheckItemTemplateState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to update an existing check item template.
  * @param data: Check item template details to be updated
  */
  UpdateCheckItemTemplateService = (data: IUpdateToolCheckItemTemplateRequest) => {
    this.addUpdateCheckItemTemplateState.inProgress = true;
    return baseService
      .putRequest(URLConstants.UpdateToolCheckItemTemplate, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateCheckItemTemplateState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addUpdateCheckItemTemplateState.success = true;
          toast.success(formatMessage('updated_success'));
        }
      })
      .catch((err: any) => {
        this.addUpdateCheckItemTemplateState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateCheckItemTemplateState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to delete an existing check item template.
  * @param id: Check item template identifier
  * @param templateName: Check item template name
  * @param tenantId: Factory (tenant) identifier being accessed
  */
  DeleteCheckItemTemplateService = (id: number, templateName: string, tenantId: number) => {
    this.deleteCheckItemTemplateState.inProgress = true;
    const url = `${URLConstants.DeleteToolCheckItemTemplate}?Id=${id}&TemplateName=${encodeURIComponent(templateName || '')}&TenantId=${tenantId}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteCheckItemTemplateState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteCheckItemTemplateState.success = true;
        }
      })
      .catch((err: any) => {
        this.deleteCheckItemTemplateState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteCheckItemTemplateState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to get a check item template detail by id from API.
  * @param id: Check item template identifier
  */
  GetCheckItemTemplateByIdService = (id: number) => {
    this.getCheckItemTemplateByIdState.inProgress = true;
    const url = `${URLConstants.GetToolCheckItemTemplateById}?Id=${id}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IToolCheckItemTemplateListVM>>
        ) => {
          if (response.data.Error) {
            this.getCheckItemTemplateByIdState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.checkItemTemplateDetail = this.mapToolCheckItemTemplateDetail(
              response.data.Data
            );
          }
        }
      )
      .catch((err: any) => {
        this.getCheckItemTemplateByIdState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getCheckItemTemplateByIdState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to map the flat GetCheckItemTemplateById response (one row per header/check-item combination) into the nested Headers -> CheckItems shape the Add/Edit form expects.
  * @param data: Flat check item template response data
  */
  mapToolCheckItemTemplateDetail = (
    data: IToolCheckItemTemplateListVM
  ): IAddToolCheckItemTemplate => {
    const headerMap = new Map<string, IToolCheckItemHeader>();
    const orderedKeys: string[] = [];

    (data.Headers || []).forEach((row: IToolCheckItemTemplateHeaderRow) => {
      const key = `${row.HeaderOrder}_${row.HeaderName}`;
      if (!headerMap.has(key)) {
        headerMap.set(key, {
          Title: row.HeaderName,
          Order: row.HeaderOrder,
          CheckItems: [],
        });
        orderedKeys.push(key);
      }
      const checkItem: IToolCheckItem = {
        Id: row.Id,
        CheckItemMessage: row.CheckitemText,
        Order: row.ItemOrder,
        IsChecked: row.AllowTextEntry,
      };
      headerMap.get(key)!.CheckItems.push(checkItem);
    });

    return {
      Id: data.Id,
      CheckItemName: data.TemplateName,
      Remarks: data.Details || '',
      Headers: orderedKeys.map((key) => headerMap.get(key)!),
    };
  };

  /**
  * This computed property is used to map checkItemTemplateList to the list suitable for the Grid Component.
  */
  get getToolCheckItemTemplateList() {
      if (this.checkItemTemplateList?.ToolCheckitemTemplateList && this.checkItemTemplateList.ToolCheckitemTemplateList?.length > 0)
          return this.checkItemTemplateList.ToolCheckitemTemplateList.map((item) => {
              let language = getLanguageFromLocalStorage();
              let isDelete = true;
              let isEdit = true;
              return {
                  Id: item.Id,
                  TemplateName: item.TemplateName,
                  Details: item.Details,
                  Language: language,
                  isDelete: isDelete,
                  isEdit: isEdit,
                  CreatedBy: item.CreatedBy,
                  UpdatedBy: item.UpdatedBy,
              }
          })
      return [];
  }

  /**
  * This function is used to reset addUpdateCheckItemTemplateState observables to their initial values.
  */
  resetAddUpdateState = () => {
    this.addUpdateCheckItemTemplateState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset deleteCheckItemTemplateState observables to their initial values.
  */
  resetDeleteState = () => {
    this.deleteCheckItemTemplateState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset checkItemTemplateDetail and getCheckItemTemplateByIdState observables to their initial values.
  */
  resetDetail = () => {
    this.checkItemTemplateDetail = AddToolCheckItemTemplateInitialState;
    this.getCheckItemTemplateByIdState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset checkItemTemplateList and checkItemTemplateDetail observables to their initial values.
  */
  reset = () => {
    this.checkItemTemplateList = getToolCheckItemTemplateListInitialState;
    this.checkItemTemplateDetail = AddToolCheckItemTemplateInitialState;
  };

  /**
  * This function is used to reset allToolCheckItemTemplates observables to their initial values.
  */
  resetAllToolCheckItemTemplatesState = () => {
    this.allToolCheckItemTemplates = [];
    this.allToolCheckItemTemplatesState = {...this.initialStateValue};
  };

  /**
  * This function is used to reset all store observables to their initial values.
  */
  resetStore = () => {
    this.checkItemTemplateList = getToolCheckItemTemplateListInitialState;
    this.addUpdateCheckItemTemplateState = { ...this.initialStateValue };
    this.deleteCheckItemTemplateState = { ...this.initialStateValue };
    this.getAllCheckItemTemplateState = { ...this.initialStateValue };
    this.checkItemTemplateDetail = AddToolCheckItemTemplateInitialState;
    this.getCheckItemTemplateByIdState = { ...this.initialStateValue };
    this.allToolCheckItemTemplatesState = { ...this.initialStateValue };
    this.allToolCheckItemTemplates = [];
  };

  /**
  * This computed property is used to build the dropdown options list from allToolCheckItemTemplates.
  */
  get allToolCheckItemTemplateOptions(): IOption[] {
        const options: IOption[] = [
          {
            id: -1,
            value: "please_select",
            disabled: false,
          },
        ];
        if (this.allToolCheckItemTemplates && this.allToolCheckItemTemplates?.length > 0)
          this.allToolCheckItemTemplates.map((item) => {
            options.push({
              id: item.Id,
              value: item.TemplateName,
              disabled: false,
            });
          });
        return options;
      }
}

export default new ToolCheckItemTemplateStore();
